import {
  Component,
  PendingTasks,
  HostListener,
  PLATFORM_ID,
  effect,
  inject,
  signal,
  computed,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import { DrupalService } from '@app/services/drupal.service';
import { DataService } from '@app/services/data.service';
import { ApiService } from '@app/services/api.service';
import { AuthService } from '@app/services/auth.service';
import { ABTestingService } from '@app/services/ab-testing.service';
import { environment } from '@src/environments/environment';
import { ComponentSharedModule } from '@app/modules/component.shared.module';
import { Route, Entity, Metatag } from '@app/models/entity.model';
import { PageNotFoundComponent } from '@app/components/page-not-found/page-not-found.component';
import { CookieConsentPageComponent } from '@app/components/cookie-consent-page/cookie-consent-page.component';
import { NodeLoaderComponent } from '@app/entities/node/loader.component';
import { NgOptimizedImage } from '@angular/common';
import Cookies from 'js-cookie';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { COOKIE_PROVIDER } from '@app/constants';
import { HeroTextComponent } from '@app/components/hero-text/hero-text.component';
import { KeyValue } from '@app/services/webform.service';
import { filter } from 'rxjs';
import { isSlowNetwork, isInSwitzerland } from '@app/helpers/fingerprinting';
import { getTrackingProductTaxonomy } from '@app/helpers/tracking-data-builders';

@Component({
  selector: 'mobi-resolver',
  templateUrl: './resolver.component.html',
  styleUrls: ['./resolver.component.scss'],
  standalone: true,
  imports: [
    ComponentSharedModule,
    RouterModule,
    PageNotFoundComponent,
    CookieConsentPageComponent,
    HeroTextComponent,
    NodeLoaderComponent,
    NgOptimizedImage,
  ],
})
export class ResolverComponent {
  notFound = signal(false);
  entity = signal<Entity>(null);
  loading = signal(true);
  hasError = signal(false);
  trackingAttributes = signal<{ [key: string]: string | null }>({});
  abTestLoading = signal(false); // Hide content while A/B test assignment is being resolved
  isAbTest = signal(false);
  showJubiAnimation = signal(false);
  shouldLoadJubiImages = signal(false); // Control when to start loading images
  jubiAnimationState = signal<'in' | 'out' | null>(null);
  jubiAnimInHideDelay = signal(false);
  jubiImageLoaded = signal(false); // Track if Jubi images loaded successfully
  #jubiAnimInStartTime: number | null = null;
  #jubiImageLoadStartTime: number | null = null; // Track when image loading starts
  #jubiImageLoadTimeout: any = null; // Timeout for image loading
  #cake: any = Cookies;
  #pageTitle: string | undefined;
  #pageDescription: string | undefined;
  #jubiAnimationTimer: any = null;
  #initialRouteSyncPending = false;
  #initialRoutePath: string | null = null;
  #initialUrlFragment: string | null = null;
  #initialFragmentRetryTimer: ReturnType<typeof setTimeout> | null = null;
  #initialFragmentRetryUntil = 0;

  #doc = inject(DOCUMENT);
  #platformId = inject(PLATFORM_ID);
  #serverCookies = inject(COOKIE_PROVIDER, { optional: true });
  #title = inject(Title);
  #router = inject(Router);
  #drupal = inject(DrupalService);
  #data = inject(DataService);
  #api = inject(ApiService);
  #auth = inject(AuthService);
  #abTesting = inject(ABTestingService);
  #meta = inject(Meta);
  #task = inject(PendingTasks);

  forceHide = computed(() => this.isAbTest() && isPlatformServer(this.#platformId));
  queryParams = computed(() => this.#drupal.queryParams());

  @HostListener('window:click', [])
  @HostListener('window:touchstart', [])
  onInteraction(): void {
    if (this.showJubiAnimation() && this.jubiAnimationState()) {
      this.#hideJubiAnimation();
    }
  }

  constructor() {
    this.#initHydrationState();
    this.#initialRoutePath =
      isPlatformBrowser(this.#platformId) &&
      (this.#drupal.hasInitialRoute() || this.#initialRouteSyncPending)
        ? this.#doc.location.pathname
        : null;

    if (isPlatformBrowser(this.#platformId)) {
      this.#initialUrlFragment = this.#getCurrentHashFragment();
    }

    effect(async () => {
      // Wait for drupal service to finish route handling
      if (!this.#drupal.routeHandled()) return;

      // If A/B testing is enabled and effect is triggered by B route
      if (
        !this.#api.loggedIn() &&
        this.#drupal.route()?.entity?.abTestEnabled &&
        this.#drupal.route()?.entity?.abTestNodeB?.id === this.entity()?.id &&
        !this.abTestLoading()
      ) {
        this.loading.set(false);
        this.#scheduleInitialFragmentScroll();
        return;
      }

      // If the route is already loaded, do nothing
      if (
        this.#drupal.route() &&
        this.entity() &&
        this.entity()?.id === this.#drupal.route()?.entity?.id &&
        !this.#initialRouteSyncPending
      ) {
        this.loading.set(false);
        this.#scheduleInitialFragmentScroll();
        return;
      }

      const cleanup = this.#task.add();
      await this.routeHandler(this.#drupal.route()!);
      this.#initialRouteSyncPending = false;
      this.#scheduleInitialFragmentScroll();
      cleanup();
    });

    // The SSR state carries no calculator links for logged-in users; fetch the
    // full route once the login state is known.
    effect(async () => {
      if (
        !this.#drupal.authLinksStripped() ||
        !this.#auth.isAuthenticated() ||
        !this.#drupal.routeHandled() ||
        !this.entity()
      ) {
        return;
      }
      this.#drupal.authLinksStripped.set(false);

      const route = await this.#drupal.routeByUrl(this.#doc.location.pathname);
      // Ignore the result if the user navigated away in the meantime.
      if (!route?.entity || route.entity.id !== this.entity()?.id) return;
      this.#drupal.route.set(route);
      this.entity.set(route.entity);
    });

    this.#router.events
      .pipe(filter((event: any) => event instanceof NavigationStart))
      .subscribe((event: any) => {
        const navEvent = event as NavigationStart;

        const nextPath = navEvent.url.split(/[?#]/, 1)[0] || '/';
        const initialPath = this.#initialRoutePath;
        this.#initialRoutePath = null;

        // Keep SSR content mounted only for the initial navigation to the same path.
        if (initialPath === nextPath) return;

        const currentPath = initialPath || this.#router.url.split(/[?#]/, 1)[0] || '/';

        // Use Angular's active URL rather than document.location: during browser
        // back/forward navigation, the browser URL changes before NavigationStart.
        if (nextPath === currentPath) {
          return;
        }

        this.#initialRouteSyncPending = false;
        this.#reset();

        this.#doc.getElementById('main')?.focus({
          preventScroll: true,
        });

        if (
          isPlatformBrowser(this.#platformId) &&
          document.location.pathname !== '/search' &&
          !navEvent.url.includes('#')
        ) {
          setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 0);
        }
      });
  }

  #getCurrentHashFragment(): string | null {
    const hash = this.#doc.location.hash;
    if (!hash || hash === '#') {
      return null;
    }

    try {
      return decodeURIComponent(hash.slice(1));
    } catch {
      return hash.slice(1);
    }
  }

  #scheduleInitialFragmentScroll(): void {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }

    if (!this.#initialUrlFragment || this.loading() || !this.entity()) {
      return;
    }

    if (this.#initialFragmentRetryTimer !== null) {
      return;
    }

    this.#initialFragmentRetryUntil = Date.now() + 5000;
    this.#runInitialFragmentScrollAttempt();
  }

  #runInitialFragmentScrollAttempt(): void {
    if (!this.#initialUrlFragment) {
      return;
    }

    const target = this.#doc.getElementById(this.#initialUrlFragment);
    if (target) {
      target.scrollIntoView({ block: 'start', behavior: 'instant' });
      this.#clearInitialFragmentScrollRetry();
      return;
    }

    if (Date.now() >= this.#initialFragmentRetryUntil) {
      this.#clearInitialFragmentScrollRetry();
      return;
    }

    this.#initialFragmentRetryTimer = setTimeout(() => {
      this.#initialFragmentRetryTimer = null;
      this.#runInitialFragmentScrollAttempt();
    }, 120);
  }

  #clearInitialFragmentScrollRetry(): void {
    if (this.#initialFragmentRetryTimer !== null) {
      clearTimeout(this.#initialFragmentRetryTimer);
      this.#initialFragmentRetryTimer = null;
    }
    this.#initialFragmentRetryUntil = 0;
    this.#initialUrlFragment = null;
  }

  #initHydrationState() {
    if (isPlatformServer(this.#platformId)) {
      return;
    }

    if (!this.#drupal.routeHandled()) {
      return;
    }

    const route = this.#drupal.route();

    // Keep the SSR content stable until the first client-side route sync completes.
    if (route?.entity) {
      this.entity.set(route.entity);
      this.loading.set(false);
      this.#initialRouteSyncPending = true;
      return;
    }

    const notFoundCodes = [404, 410, 403];
    if (!route?.entity && notFoundCodes.includes(this.#api.responseStatus)) {
      this.notFound.set(true);
      this.loading.set(false);
      this.#initialRouteSyncPending = true;
    }
  }

  getConsent(entity: Entity): boolean {
    let cookie: any = '';
    if (isPlatformServer(this.#platformId)) {
      cookie = this.#serverCookies
        ? this.#serverCookies[`mobi-${entity?.cookieWall}-am-accepted`]
        : '';
    } else {
      cookie = this.#cake.get(`mobi-${entity?.cookieWall}-am-accepted`);
    }

    return cookie === 'true';
  }

  async acceptCookies() {
    this.loading.set(true);
    !this.#cake.set(`mobi-${this.entity()?.cookieWall}-am-accepted`, 'true', {
      expires: 365,
      secure: true,
    });
    window.location.reload();
  }

  async routeHandler(route: Route) {
    let entity: Entity = route?.entity || null;
    const metaTags: Metatag[] | undefined = route?.metatags;
    this.isAbTest.set(false);

    // start: jubi logic
    if (entity?.isJubi) {
      this.#doc.body.classList.add(`is-jubi-page`);
      this.#showJubiAnimationIfNeeded();
    } else {
      if (this.#doc.body.classList.contains(`is-jubi-page`)) {
        this.#doc.body.classList.remove(`is-jubi-page`);
      }
      this.showJubiAnimation.set(false);
    }
    // end: jubi logic

    // If the entity is not found
    const notFoundCodes = [404, 410, 403];
    if (!entity && notFoundCodes.includes(this.#api.responseStatus)) {
      this.notFound.set(true);
      this.loading.set(false);
      return;
    }

    // If something else went wrong
    if (!entity) {
      this.notFound.set(false);
      this.loading.set(false);
      return;
    }

    // Handle A/B testing if enabled (only in browser and if not logged in)
    // SSR always renders variant A, then browser hides content while resolving assignment
    if (!this.#api.loggedIn() && entity.abTestEnabled && entity.abTestNodeB) {
      this.isAbTest.set(true);
      if (isPlatformBrowser(this.#platformId)) {
        this.abTestLoading.set(true);

        try {
          const assignment = await this.#abTesting.getAssignment(
            entity.id,
            entity.abTestSplitRatio ?? 50
          );

          if (assignment === 'B' && entity.abTestNodeB.id !== entity.id) {
            const nodeBEntity = await this.#data.loadEntityById(entity.abTestNodeB.id);
            if (nodeBEntity) {
              entity = nodeBEntity;
              this.entity.set(entity);
              this.#drupal.route.set({
                ...route,
                entity: {
                  ...nodeBEntity,
                  translations: route.entity?.translations,
                  abTestNodeB: route.entity?.abTestNodeB,
                  abTestEnabled: route.entity?.abTestEnabled,
                  abTestId: route.entity?.abTestId || `mobi_ab_${route.entity?.id}`,
                },
              });
            }
          }
        } catch (error) {
          console.error('A/B testing error, falling back to version A:', error);
        } finally {
          this.abTestLoading.set(false);
        }
      }
    }

    // If entity is behind cookie wall and the consent is already given, load entity
    if (entity?.cookieWall && this.getConsent(entity)) {
      this.#reset();
      const loadedEntity = await this.#data.loadEntityById(entity.id);
      this.entity.set(loadedEntity);
    }

    // Check for Meta tags and get page title
    if (metaTags) {
      const pageTitleTag = metaTags.find(
        (tag: any) => tag?.attributes?.[0]?.value === 'title'
      );
      this.#pageTitle = pageTitleTag?.attributes?.[1]?.value;

      const pageDescriptionTag = metaTags.find(
        (tag: any) => tag?.attributes?.[0]?.value === 'description'
      );
      this.#pageDescription = pageDescriptionTag?.attributes?.[1]?.value;
    }

    const platformName = (environment.platformNames as any)[entity!.language];
    const teaserText = entity?.teaserText?.replace(/<\/?[^>]+(>|$)/g, '');
    const trackingAttributes: { [key: string]: string | null } = {};
    const entityForTracking: any = entity as any;
    trackingAttributes['component-type'] = 'node';
    trackingAttributes['page-bundle'] = entity?.bundle || null;

    if (entity?.uuid) {
      trackingAttributes['component-id'] = entity.uuid;
    }
    if (entity?.bundle) {
      trackingAttributes['component-name'] = entity.bundle;
    }

    const trackingProduct = getTrackingProductTaxonomy(
      entityForTracking?.taxonomyProduct
    );

    if (trackingProduct) {
      trackingAttributes['product-name'] = trackingProduct.trackingName;
      trackingAttributes['product-id'] = trackingProduct.trackingId;
    }

    if (entityForTracking?.taxonomyContentphase?.length > 0) {
      trackingAttributes['page-contentphase'] = entityForTracking.taxonomyContentphase
        .map((item: any) => item?.label)
        .filter(Boolean)
        .join(', ');
    }

    if (entityForTracking?.taxonomySegment?.length > 0) {
      trackingAttributes['page-segment'] = entityForTracking.taxonomySegment
        .map((item: any) => item?.label)
        .filter(Boolean)
        .join(', ');
    }

    if (entityForTracking?.taxonomySubject?.length > 0) {
      trackingAttributes['page-subject'] = entityForTracking.taxonomySubject
        .map((item: any) => item?.label)
        .filter(Boolean)
        .join(', ');
    }

    // Set tracking attributes based on additional tealium values
    if (entity?.tealiumAddv?.length) {
      const variant = entity.tealiumAddv!.find(
        (v: KeyValue) => v.key === 'product_variant'
      );
      const name = entity.tealiumAddv!.find((v: KeyValue) => v.key === 'product_name');
      const id = entity.tealiumAddv!.find((v: KeyValue) => v.key === 'product_id');
      const compId = entity.tealiumAddv!.find((v: KeyValue) => v.key === 'component_id');
      const compType = entity.tealiumAddv!.find(
        (v: KeyValue) => v.key === 'component_type'
      );
      const compName = entity.tealiumAddv!.find(
        (v: KeyValue) => v.key === 'component_name'
      );
      const pageBundle = entity.tealiumAddv!.find(
        (v: KeyValue) => v.key === 'page_bundle'
      );
      const pageContentphase = entity.tealiumAddv!.find(
        (v: KeyValue) => v.key === 'page_contentphase'
      );
      const pageSegment = entity.tealiumAddv!.find(
        (v: KeyValue) => v.key === 'page_segment'
      );
      const pageSubject = entity.tealiumAddv!.find(
        (v: KeyValue) => v.key === 'page_subject'
      );
      trackingAttributes['product-variant'] = variant?.value || null;
      trackingAttributes['product-name'] = name?.value || null;
      trackingAttributes['product-id'] = id?.value || trackingAttributes['product-id'];
      trackingAttributes['component-id'] =
        compId?.value || trackingAttributes['component-id'];
      trackingAttributes['component-type'] =
        compType?.value || trackingAttributes['component-type'];
      trackingAttributes['component-name'] =
        compName?.value || trackingAttributes['component-name'];
      trackingAttributes['page-bundle'] =
        pageBundle?.value || trackingAttributes['page-bundle'];
      trackingAttributes['page-contentphase'] =
        pageContentphase?.value || trackingAttributes['page-contentphase'];
      trackingAttributes['page-segment'] =
        pageSegment?.value || trackingAttributes['page-segment'];
      trackingAttributes['page-subject'] =
        pageSubject?.value || trackingAttributes['page-subject'];
    }

    this.trackingAttributes.set(trackingAttributes);

    // Set Page Title with fallback
    this.#pageTitle
      ? this.#title.setTitle(this.#pageTitle)
      : this.#title.setTitle(`${entity!.title} | ${platformName}`);
    this.#meta.addTag({
      name: 'description',
      content: `${this.#pageDescription ? this.#pageDescription : teaserText}`,
    });

    const hydratedEntity = this.entity();
    const keepHydratedEntity =
      this.#initialRouteSyncPending &&
      !!hydratedEntity &&
      !!entity &&
      hydratedEntity.id === entity.id &&
      !entity.abTestEnabled &&
      !entity.cookieWall;

    // Keep the SSR object reference for the first browser sync when data is equivalent.
    if (!keepHydratedEntity) {
      this.entity.set(entity);
    }
    this.loading.set(false);
  }

  #reset() {
    this.loading.set(true);
    this.entity.set(null);
    this.notFound.set(false);
    this.hasError.set(false);
    this.#clearInitialFragmentScrollRetry();

    // Clean up Jubi image load timeout and reset image loading state
    if (this.#jubiImageLoadTimeout) {
      clearTimeout(this.#jubiImageLoadTimeout);
      this.#jubiImageLoadTimeout = null;
    }
    this.shouldLoadJubiImages.set(false);
    this.jubiImageLoaded.set(false);
  }

  #showJubiAnimationIfNeeded() {
    if (isPlatformBrowser(this.#platformId)) {
      // Only show animation if:
      // 1. User is in Switzerland
      // 2. Not on a slow network (4G or slower)
      // 3. Image loads within 5 seconds (checked when image loads)
      if (!isInSwitzerland()) {
        return;
      }

      if (isSlowNetwork()) {
        return;
      }

      const hasShownAnimation = sessionStorage.getItem('jubiAnimationShown') === 'true';
      if (!hasShownAnimation) {
        // All conditions passed, start loading images
        this.showJubiAnimation.set(true);
        this.shouldLoadJubiImages.set(true);

        // Start tracking image load time
        this.#jubiImageLoadStartTime = Date.now();

        // Set timeout to prevent showing animation if image takes too long
        this.#jubiImageLoadTimeout = setTimeout(() => {
          if (!this.jubiImageLoaded()) {
            // Image didn't load within 5 seconds, hide animation and stop loading
            this.showJubiAnimation.set(false);
            this.shouldLoadJubiImages.set(false);
            this.#jubiImageLoadStartTime = null;
          }
        }, 5000);
      }
    }
  }

  onJubiImageLoad() {
    // Called when the first Jubi image loads successfully
    if (this.#jubiImageLoadStartTime) {
      const loadTime = Date.now() - this.#jubiImageLoadStartTime;

      // Clear the timeout
      if (this.#jubiImageLoadTimeout) {
        clearTimeout(this.#jubiImageLoadTimeout);
        this.#jubiImageLoadTimeout = null;
      }

      // Only proceed if image loaded within 5 seconds
      if (loadTime <= 5000) {
        this.jubiImageLoaded.set(true);

        // Show animation immediately
        this.jubiAnimationState.set('in');
        this.#jubiAnimInStartTime = Date.now();
        sessionStorage.setItem('jubiAnimationShown', 'true');

        // Auto-hide after animation completes + 2 seconds if no interaction
        setTimeout(() => {
          if (this.jubiAnimationState() === 'in') {
            this.#hideJubiAnimation();
          }
        }, 2000 + 1500);
      } else {
        // Image loaded too late, hide the animation
        this.showJubiAnimation.set(false);
      }
    }
  }

  #hideJubiAnimation() {
    if (this.#jubiAnimationTimer) {
      clearTimeout(this.#jubiAnimationTimer);
      this.#jubiAnimationTimer = null;
    }

    const playAnimationOut = () => {
      this.jubiAnimationState.set('out');
      // Delay hiding the 'in' animation by 750ms
      setTimeout(() => {
        this.jubiAnimInHideDelay.set(true);
      }, 750);
      // Hide everything after out animation completes
      setTimeout(() => {
        this.showJubiAnimation.set(false);
        this.jubiAnimationState.set(null);
        this.jubiAnimInHideDelay.set(false);
        this.#jubiAnimInStartTime = null;
      }, 2000);
    };

    // Ensure animation-in has played for at least 2000ms
    if (this.#jubiAnimInStartTime) {
      const elapsed = Date.now() - this.#jubiAnimInStartTime;
      const remaining = 2000 - elapsed;

      if (remaining > 0) {
        // Wait for animation-in to complete
        this.#jubiAnimationTimer = setTimeout(() => {
          playAnimationOut();
        }, remaining);
      } else {
        // Animation-in already completed
        playAnimationOut();
      }
    } else {
      // No start time recorded, play out animation
      playAnimationOut();
    }
  }
}
